import axiosInstanceToken from '@/lib/axiosInstanceToken';
import { AxiosPromise } from 'axios';
import {
  ListSSHInstancesResponse,
  RunSSHPayload,
  TaskActionResponse,
  TaskLogsResponse,
  TerminateSSHResponse,
  ValidateModelPayload,
  ValidateModelResponse,
  ModelSupportMatrixResponse,
  ModelSupportMatrixError,
} from './type';

export const listSSHInstances = async (
  token: string
): Promise<ListSSHInstancesResponse> => {
  const baseUrl = axiosInstanceToken.defaults.baseURL || '';
  const response = await fetch(`${baseUrl}/task/ssh/list`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const runSSHInstanceSSE = async (
  payload: RunSSHPayload,
  token: string
): Promise<Response> => {
  const baseUrl = axiosInstanceToken.defaults.baseURL || '';
  const response = await fetch(`${baseUrl}/task/run/ssh`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response;
};

export const cancelSSHInstance = async (
  taskId: string,
  token: string,
  reason: string = 'user_requested'
): Promise<{ status: string; task_id: string; details: string }> => {
  const baseUrl = axiosInstanceToken.defaults.baseURL || '';
  const response = await fetch(`${baseUrl}/task/cancel/${taskId}?reason=${encodeURIComponent(reason)}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const stopSSHInstance = async (
  taskId: string,
  token: string
): Promise<TaskActionResponse> => {
  const baseUrl = axiosInstanceToken.defaults.baseURL || '';
  const response = await fetch(`${baseUrl}/task/ssh/${taskId}/stop`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const resumeSSHInstance = async (
  taskId: string,
  token: string
): Promise<TaskActionResponse> => {
  const baseUrl = axiosInstanceToken.defaults.baseURL || '';
  const response = await fetch(`${baseUrl}/task/ssh/${taskId}/resume`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const renameSSHInstance = async (
  projectId: string,
  projectName: string,
  token: string
): Promise<any> => {
  const baseUrl = axiosInstanceToken.defaults.baseURL || '';
  const response = await fetch(`${baseUrl}/project/${projectId}/to/${encodeURIComponent(projectName)}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const terminateSSHInstance = async (
  taskId: string,
  token: string
): Promise<TerminateSSHResponse> => {
  const baseUrl = axiosInstanceToken.defaults.baseURL || '';
  const response = await fetch(`${baseUrl}/task/ssh/${taskId}/terminate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const fetchTaskLogs = async (
  taskId: string,
  token: string,
  params?: { limit?: number; start?: string }
): Promise<TaskLogsResponse> => {
  const baseUrl = axiosInstanceToken.defaults.baseURL || '';
  const searchParams = new URLSearchParams();
  if (params?.limit) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params?.start) {
    searchParams.set('start', params.start);
  }
  const queryString = searchParams.toString();
  const url = `${baseUrl}/task/instance-logs/${taskId}${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const validateModel = async (
  payload: ValidateModelPayload,
  token: string
): Promise<ValidateModelResponse> => {
  const baseUrl = axiosInstanceToken.defaults.baseURL || '';
  const response = await fetch(`${baseUrl}/model/validate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

/**
 * Get model support matrix from HuggingFace repository
 * GET /model/support-matrix?repo={owner/repo}
 */
export const getModelSupportMatrix = async (
  repo: string,
  token: string
): Promise<ModelSupportMatrixResponse | ModelSupportMatrixError> => {
  const baseUrl = axiosInstanceToken.defaults.baseURL || '';
  const response = await fetch(
    `${baseUrl}/model/support-matrix?repo=${encodeURIComponent(repo)}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }
  );

  const data = await response.json();

  // API returns 400 for invalid format, 404 for not found
  // Both return { error: string, repo: string }
  if (!response.ok) {
    return data as ModelSupportMatrixError;
  }

  return data as ModelSupportMatrixResponse;
};
